/**
 * Authoritative character workflow node catalog (frontend).
 *
 * Availability:
 * - available: real platform path (persist, queue, history, files)
 * - worker_mock: queues to workers but returns labeled mock payloads (not production art)
 * - unavailable: no established executable contract yet — Run is disabled
 *
 * Infrastructure-only nodes (dev_worker) stay out of CHARACTER_CATALOG.
 */

export const PORT_TYPES = {
  text: { id: 'text', label: 'Text', color: '#4a7ca4' },
  image: { id: 'image', label: 'Image', color: '#39ced4' },
  model: { id: 'model', label: '3D', color: '#ee604d' },
  material: { id: 'material', label: 'Material', color: '#a450db' },
  any: { id: 'any', label: 'Any', color: '#8c8b96' },
};

/** @typedef {'available'|'worker_mock'|'unavailable'} CapabilityStatus */

/**
 * @type {Record<string, object>}
 */
export const CHARACTER_NODE_CATALOG = {
  text: {
    type: 'text',
    label: 'Character brief',
    purpose: 'Written brief, notes, or instructions for downstream operations.',
    category: 'inputs',
    activity: 'Gather materials',
    implementationVersion: '1',
    availability: 'available',
    executable: false,
    ports: {
      inputs: [{ id: 'in', label: 'Context', type: 'text', required: false, max: 8 }],
      outputs: [{ id: 'out', label: 'Brief', type: 'text', required: false }],
    },
    settings: [],
    resultPresentation: 'text',
  },
  image: {
    type: 'image',
    label: 'Image',
    purpose:
      'Project image reference with optional per-image instructions. One node type covers image-only and image+text.',
    category: 'inputs',
    activity: 'Gather materials',
    implementationVersion: '1',
    availability: 'available',
    executable: false,
    ports: {
      inputs: [],
      outputs: [{ id: 'out', label: 'Image', type: 'image', required: false }],
    },
    settings: [],
    resultPresentation: 'image',
    acceptsInstructions: true,
  },
  reference: {
    type: 'reference',
    label: 'Project material',
    purpose: 'Reference another character or story record brief in this project.',
    category: 'inputs',
    activity: 'Gather materials',
    implementationVersion: '1',
    availability: 'available',
    executable: false,
    ports: {
      inputs: [],
      outputs: [{ id: 'out', label: 'Reference', type: 'text', required: false }],
    },
    settings: [],
    resultPresentation: 'text',
  },
  combine: {
    type: 'combine',
    label: 'Combine',
    purpose: 'Assemble compatible connected materials for an operation.',
    category: 'utilities',
    activity: 'Organize',
    implementationVersion: '1',
    availability: 'available',
    executable: false,
    ports: {
      inputs: [{ id: 'in', label: 'Materials', type: 'any', required: true, max: 16 }],
      outputs: [{ id: 'out', label: 'Bundle', type: 'any', required: false }],
    },
    settings: [],
    resultPresentation: 'text',
  },
  create_images: {
    type: 'create_images',
    label: 'Generate Concepts',
    purpose: 'Generate concept image drafts from briefs and image references.',
    category: 'processing',
    activity: 'Concept',
    implementationVersion: '1',
    availability: 'worker_mock',
    availabilityDetail:
      'Worker create_images currently returns labeled mock image payloads. Not a production art provider.',
    executable: true,
    operation: 'create_images',
    ports: {
      inputs: [
        { id: 'brief', label: 'Brief', type: 'text', required: false, max: 4 },
        { id: 'references', label: 'References', type: 'image', required: false, max: 8 },
      ],
      outputs: [{ id: 'concepts', label: 'Concepts', type: 'image', required: false }],
    },
    settings: [
      { key: 'aspect', label: 'Aspect', default: '1:1' },
      { key: 'count', label: 'Count', default: '1' },
    ],
    resultPresentation: 'image',
    allowsMultipleInstances: true,
  },
  worker: {
    type: 'worker',
    label: 'Image to prompt',
    purpose: 'Derive text prompts/instructions from connected image materials.',
    category: 'processing',
    activity: 'Concept',
    implementationVersion: '1',
    availability: 'worker_mock',
    availabilityDetail:
      'Worker operation returns labeled mock text. Prefer real provider registration when available.',
    executable: true,
    operation: 'worker',
    ports: {
      inputs: [
        { id: 'images', label: 'Images', type: 'image', required: true, max: 8 },
        { id: 'notes', label: 'Notes', type: 'text', required: false, max: 4 },
      ],
      outputs: [{ id: 'prompt', label: 'Prompt', type: 'text', required: false }],
    },
    settings: [{ key: 'tone', label: 'Tone', default: 'neutral' }],
    resultPresentation: 'text',
    allowsMultipleInstances: true,
  },
  create_ortho: {
    type: 'create_ortho',
    label: 'Orthographic views',
    purpose: 'Generate front/side/back orthographic views from an approved concept.',
    category: 'processing',
    activity: 'Views',
    implementationVersion: '0',
    availability: 'unavailable',
    availabilityDetail:
      'No registered worker operation for orthographic generation. Stage cards in Studio are UI-only.',
    executable: false,
    operation: null,
    ports: {
      inputs: [{ id: 'concept', label: 'Concept', type: 'image', required: true, max: 1 }],
      outputs: [{ id: 'views', label: 'Views', type: 'image', required: false }],
    },
    settings: [],
    resultPresentation: 'image',
    allowsMultipleInstances: true,
  },
  create_3d: {
    type: 'create_3d',
    label: 'Generate mesh',
    purpose: 'Generate a 3D mesh from connected views/concepts.',
    category: 'processing',
    activity: 'Mesh',
    implementationVersion: '1',
    availability: 'worker_mock',
    availabilityDetail:
      'Worker create_3d returns a mock artifact card without mesh bytes. Not equivalent to USD export.',
    executable: true,
    operation: 'create_3d',
    ports: {
      inputs: [
        { id: 'views', label: 'Views', type: 'image', required: false, max: 8 },
        { id: 'brief', label: 'Brief', type: 'text', required: false, max: 4 },
      ],
      outputs: [{ id: 'mesh', label: 'Mesh', type: 'model', required: false }],
    },
    settings: [{ key: 'format', label: 'Format', default: 'glb' }],
    resultPresentation: 'model',
    allowsMultipleInstances: true,
  },
  texture_pbr: {
    type: 'texture_pbr',
    label: 'Texture / PBR maps',
    purpose: 'Paint textures and produce PBR maps for an approved mesh.',
    category: 'processing',
    activity: 'Look',
    implementationVersion: '0',
    availability: 'unavailable',
    availabilityDetail: 'No worker texture/PBR operation is registered on the platform.',
    executable: false,
    ports: {
      inputs: [{ id: 'mesh', label: 'Mesh', type: 'model', required: true, max: 1 }],
      outputs: [{ id: 'maps', label: 'Maps', type: 'material', required: false }],
    },
    settings: [],
    resultPresentation: 'image',
    allowsMultipleInstances: true,
  },
  rig_skin: {
    type: 'rig_skin',
    label: 'Rig / skin',
    purpose: 'Generate a rig and clean skinning for an approved mesh.',
    category: 'processing',
    activity: 'Rig',
    implementationVersion: '0',
    availability: 'unavailable',
    availabilityDetail:
      'Maya/FabricatorStudio rigging jobs exist as app contracts but are not wired into this character canvas operation.',
    executable: false,
    ports: {
      inputs: [
        { id: 'mesh', label: 'Mesh', type: 'model', required: true, max: 1 },
        { id: 'maps', label: 'Maps', type: 'material', required: false, max: 4 },
      ],
      outputs: [{ id: 'character', label: 'Character', type: 'model', required: false }],
    },
    settings: [],
    resultPresentation: 'model',
    allowsMultipleInstances: true,
  },
  export_character: {
    type: 'export_character',
    label: 'Export character',
    purpose: 'Package explicitly approved artifact versions into a durable export linked to a checkpoint.',
    category: 'outputs',
    activity: 'Finish',
    implementationVersion: '1',
    availability: 'available',
    availabilityDetail:
      'Packages approved graph selections into a versioned JSON manifest + referenced project files. OpenUSD Character Stage viewing is a separate demo asset path, not this export.',
    executable: false,
    ports: {
      inputs: [
        { id: 'character', label: 'Approved character', type: 'any', required: true, max: 8 },
      ],
      outputs: [],
    },
    settings: [{ key: 'format', label: 'Format', default: 'bakeonator_package_v1' }],
    resultPresentation: 'report',
  },
  output: {
    type: 'output',
    label: 'Output',
    purpose: 'Compatibility endpoint for assembled material.',
    category: 'outputs',
    activity: 'Finish',
    implementationVersion: '1',
    availability: 'available',
    executable: false,
    ports: {
      inputs: [{ id: 'in', label: 'In', type: 'any', required: false, max: 16 }],
      outputs: [],
    },
    settings: [],
    resultPresentation: 'text',
    legacyCompatible: true,
  },
};

export const CHARACTER_ACTIVITIES = [
  'Gather materials',
  'Organize',
  'Concept',
  'Views',
  'Mesh',
  'Look',
  'Rig',
  'Finish',
];

export function getCatalogDef(type) {
  return CHARACTER_NODE_CATALOG[type] || null;
}

export function characterPickerDefs({ includeUnavailable = true } = {}) {
  return Object.values(CHARACTER_NODE_CATALOG).filter((def) => {
    if (def.adminOnly) return false;
    if (!includeUnavailable && def.availability === 'unavailable') return false;
    return true;
  });
}

export function portsCompatible(outType, inType) {
  if (!outType || !inType) return false;
  if (inType === 'any' || outType === 'any') return true;
  return outType === inType;
}

export function defaultPortsForType(type) {
  const def = getCatalogDef(type);
  if (def?.ports) return def.ports;
  return {
    inputs: [{ id: 'in', label: 'In', type: 'any', required: false, max: 16 }],
    outputs: [{ id: 'out', label: 'Out', type: 'any', required: false }],
  };
}

/** Missing required inputs for an operation node given current edges. */
export function missingRequiredPorts(graph, node) {
  const def = getCatalogDef(node.type);
  if (!def) return [];
  const missing = [];
  for (const port of def.ports?.inputs || []) {
    if (!port.required) continue;
    const count = (graph.edges || []).filter(
      (e) => e.target === node.id && (e.target_port || 'in') === port.id
    ).length;
    if (count < 1) missing.push(port);
  }
  return missing;
}

export function canExecuteCatalogNode(node, graph) {
  const def = getCatalogDef(node.type);
  if (!def?.executable) {
    return { ok: false, reason: def?.availabilityDetail || 'This node is not executable.' };
  }
  if (def.availability === 'unavailable') {
    return { ok: false, reason: def.availabilityDetail || 'Capability unavailable.' };
  }
  const missing = missingRequiredPorts(graph, node);
  if (missing.length) {
    return {
      ok: false,
      reason: `Missing required input: ${missing.map((p) => p.label).join(', ')}.`,
      missing,
    };
  }
  return { ok: true, reason: '', mock: def.availability === 'worker_mock' };
}
